// IndexedDB → Supabase migration helper (TS port of migrate-supabase.js).
// Only runs when local + remote DBs are both present.

import type { RemoteDb } from './remote';

interface LocalDbLike {
  getAll<T = unknown>(table: string): Promise<T[]>;
  bulkPut<T = unknown>(table: string, records: T[]): Promise<number>;
}

export const MIGRATION_ORDER = [
  'provinces',
  'districts',
  'indicators',
  'recommendations',
  'assets',
  'yearly_observations',
  'bwm_experts',
  'bwm_inputs',
  'bwm_weights',
  'interviews',
  'fgds',
  'field_observations',
  'documents',
  'scores',
  'settings',
  'audit_logs',
  'raw_asset_data',
] as const;

export interface MigrateReport {
  started_at: string;
  finished_at?: string;
  mode: 'live' | 'dry-run';
  tables: Record<
    string,
    { local_count: number; pushed: number; dry_run?: boolean; error?: string }
  >;
  grand_total: number;
  errors: Array<{ table: string; msg: string }>;
}

export interface MigrateOpts {
  dryRun?: boolean;
  tables?: string[];
}

export async function migrateLocalToRemote(
  local: LocalDbLike,
  remote: RemoteDb,
  onLog: (line: string) => void,
  opts: MigrateOpts = {},
): Promise<MigrateReport> {
  const log = (m: string) => {
    onLog(m);
    console.info('[migrate]', m);
  };
  const tables = opts.tables?.length ? opts.tables : (MIGRATION_ORDER as readonly string[]);
  const report: MigrateReport = {
    started_at: new Date().toISOString(),
    mode: opts.dryRun ? 'dry-run' : 'live',
    tables: {},
    grand_total: 0,
    errors: [],
  };
  for (const t of tables) {
    log(`▶ ${t} …`);
    try {
      const records = await local.getAll(t);
      if (!records.length) {
        log('  (kosong, skip)');
        report.tables[t] = { local_count: 0, pushed: 0 };
        continue;
      }
      log(`  ${records.length} record di IndexedDB`);
      if (opts.dryRun) {
        report.tables[t] = { local_count: records.length, pushed: 0, dry_run: true };
        continue;
      }
      const pushed = await remote.bulkPut(t, records);
      log(`  ✓ ${pushed} record di-upsert`);
      report.tables[t] = { local_count: records.length, pushed };
      report.grand_total += pushed;
    } catch (e) {
      const msg = (e as Error).message;
      log(`  ✗ gagal: ${msg}`);
      report.tables[t] = { local_count: 0, pushed: 0, error: msg };
      report.errors.push({ table: t, msg });
    }
  }
  report.finished_at = new Date().toISOString();
  log(`Selesai. Total: ${report.grand_total}. Errors: ${report.errors.length}.`);
  return report;
}

export async function pullRemoteToLocal(
  local: LocalDbLike,
  remote: RemoteDb,
  onLog: (line: string) => void,
): Promise<{ pulled: Record<string, number>; errors: Array<{ table: string; msg: string }> }> {
  const log = (m: string) => {
    onLog(m);
    console.info('[pull]', m);
  };
  const out: { pulled: Record<string, number>; errors: Array<{ table: string; msg: string }> } = {
    pulled: {},
    errors: [],
  };
  for (const t of MIGRATION_ORDER) {
    try {
      const rows = await remote.getAll(t);
      log(`${t}: ${rows.length} record`);
      if (rows.length) await local.bulkPut(t, rows);
      out.pulled[t] = rows.length;
    } catch (e) {
      out.errors.push({ table: t, msg: (e as Error).message });
    }
  }
  return out;
}
